import {
  Divider,
  Input,
  Spinner,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ClipboardTaskListLtrRegular,
  DocumentBulletListRegular,
  PersonRegular,
  SearchRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type JSX } from 'react';
import { useNavigate } from 'react-router';

import { api, type SearchResult } from '@/api/endpoints';
import { DOC_PAGES } from '@/docs/content';

const useStyles = makeStyles({
  wrap: { position: 'relative', flexGrow: 1, maxWidth: '640px' },
  input: {
    width: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  popover: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    boxShadow: tokens.shadow16,
    maxHeight: '420px',
    overflowY: 'auto',
    ...shorthands.padding('4px', '0'),
  },
  groupLabel: {
    ...shorthands.padding('6px', '12px', '2px'),
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    display: 'block',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '10px',
    width: '100%',
    ...shorthands.padding('6px', '12px'),
    ...shorthands.border('0'),
    backgroundColor: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover },
  },
  itemActive: { backgroundColor: tokens.colorSubtleBackgroundHover },
  icon: { fontSize: '16px', color: tokens.colorBrandForeground1, flexShrink: 0 },
  itemText: { display: 'flex', flexDirection: 'column', minWidth: 0, rowGap: '1px' },
  // Explicit colours: the button resets inherited colour, which left the title
  // rendering lighter than its own subtitle.
  title: { color: tokens.colorNeutralForeground1, fontSize: tokens.fontSizeBase300 },
  subtitle: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  empty: { ...shorthands.padding('16px', '12px'), color: tokens.colorNeutralForeground3 },
});

const ICON: Record<string, JSX.Element> = {
  recipient: <PersonRegular />,
  message: <DocumentBulletListRegular />,
  template: <ClipboardTaskListLtrRegular />,
  doc: <SearchRegular />,
};

const GROUP_LABEL: Record<string, string> = {
  recipient: 'Recipients',
  message: 'Messages',
  template: 'Templates',
  doc: 'Documentation',
};

type Hit = SearchResult | { type: 'doc'; id: string; title: string; subtitle: string; href: string };

/**
 * The portal's global search: one box in the header that jumps straight to a
 * record. Server-side for recipients, messages and templates; documentation is
 * matched in the browser because those pages are static.
 */
export function GlobalSearch() {
  const styles = useStyles();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Debounced so typing a phone number does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const onClickAway = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  const remote = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.search(debounced),
    enabled: debounced.length >= 2,
    staleTime: 10_000,
  });

  const docHits: Hit[] =
    debounced.length >= 2
      ? DOC_PAGES.filter(
          (p) =>
            p.title.toLowerCase().includes(debounced.toLowerCase()) ||
            p.keywords.includes(debounced.toLowerCase()),
        )
          .slice(0, 4)
          .map((p) => ({
            type: 'doc' as const,
            id: p.id,
            title: p.title,
            subtitle: p.group,
            href: `/docs?page=${p.id}`,
          }))
      : [];

  const hits: Hit[] = [...(remote.data?.results ?? []), ...docHits];
  const grouped = hits.reduce<Record<string, Hit[]>>((acc, h) => {
    (acc[h.type] ??= []).push(h);
    return acc;
  }, {});
  const flat = Object.values(grouped).flat();

  const go = (hit: Hit) => {
    setOpen(false);
    setQuery('');
    void navigate(hit.href);
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <Input
        className={styles.input}
        size="medium"
        contentBefore={<SearchRegular />}
        placeholder="Search recipients, messages, templates and docs"
        aria-label="Global search"
        value={query}
        onChange={(_e, d) => {
          setQuery(d.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => query.length > 0 && setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          if (!flat.length) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => (i + 1) % flat.length);
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => (i - 1 + flat.length) % flat.length);
          }
          if (e.key === 'Enter' && flat[active]) {
            e.preventDefault();
            go(flat[active]);
          }
        }}
      />

      {open && debounced.length >= 2 && (
        <div className={styles.popover} role="listbox" aria-label="Search results">
          {remote.isLoading ? (
            <div className={styles.empty}>
              <Spinner size="tiny" label="Searching…" />
            </div>
          ) : flat.length === 0 ? (
            <div className={styles.empty}>
              <Text size={300}>No results for “{debounced}”.</Text>
            </div>
          ) : (
            Object.entries(grouped).map(([type, items], gi) => (
              <div key={type}>
                {gi > 0 && <Divider appearance="subtle" />}
                <span className={styles.groupLabel}>{GROUP_LABEL[type] ?? type}</span>
                {items.map((hit) => {
                  const index = flat.indexOf(hit);
                  return (
                    <button
                      key={`${hit.type}-${hit.id}`}
                      type="button"
                      role="option"
                      aria-selected={index === active}
                      className={`${styles.item} ${index === active ? styles.itemActive : ''}`}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => go(hit)}
                    >
                      <span className={styles.icon}>{ICON[hit.type]}</span>
                      <span className={styles.itemText}>
                        <Text truncate wrap={false} className={styles.title}>
                          {hit.title}
                        </Text>
                        <Text truncate wrap={false} className={styles.subtitle}>
                          {hit.subtitle}
                        </Text>
                      </span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
